"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ReactPayPalScriptOptions,
  PayPalScriptProvider,
  usePayPalScriptReducer,
  FUNDING,
} from "@paypal/react-paypal-js";
import { CartResponse, fetchCart } from "@/networking/carts.api";
import { useMemo } from "react";
import { isApplePaySubscriptionEnabled } from "@/lib/apple-pay-recurring";

interface PaypalContextType {
  shouldShowVenmo: boolean;
  shouldShowPayLater: boolean;
  shouldShowApplePay: boolean;
  isProviderReady: boolean;
  isRejected: boolean;
  isDecryptedApplePayViaPaypal?: boolean | null;
}

const PaypalContext = createContext<PaypalContextType>({
  shouldShowVenmo: false,
  shouldShowPayLater: false,
  shouldShowApplePay: false,
  isProviderReady: false,
  isRejected: false,
  isDecryptedApplePayViaPaypal: null,
});

export const usePaypalContext = () => {
  const context = useContext(PaypalContext);
  if (!context) {
    throw new Error("usePaypalContext must be used within a PaypalProvider");
  }
  return context;
};

// Inner component that can safely use usePayPalScriptReducer
function PaypalProviderInner({
  children,
  shouldShowVenmo: shouldShowVenmoProp,
  shouldShowPayLater,
  shouldShowApplePay: shouldShowApplePayProp,
  isDecryptedApplePayViaPaypal,
}: {
  children: React.ReactNode;
  shouldShowVenmo: boolean;
  shouldShowPayLater: boolean;
  shouldShowApplePay: boolean;
  isDecryptedApplePayViaPaypal?: boolean | null;
}) {
  const [{ isRejected, isResolved }] = usePayPalScriptReducer();
  const [isVenmoEligible, setIsVenmoEligible] = useState(false);
  const [hasCheckedVenmo, setHasCheckedVenmo] = useState(false);
  const [isApplePayEligible, setIsApplePayEligible] = useState(false);
  const [hasCheckedApplePay, setHasCheckedApplePay] = useState(false);

  useEffect(() => {
    if (!isResolved || hasCheckedVenmo) return;

    const checkVenmoEligibility = () => {
      try {
        const windowObj = window as unknown as Record<string, unknown>;
        const paypal = windowObj.paypal_checkout_global || windowObj.paypal;

        if (
          paypal &&
          typeof (paypal as Record<string, unknown>).isFundingEligible ===
            "function"
        ) {
          const eligible = (
            paypal as { isFundingEligible: (funding: string) => boolean }
          ).isFundingEligible(FUNDING.VENMO);
          setIsVenmoEligible(eligible);
        }
      } catch (e) {
        console.error("Venmo eligibility check failed:", e);
        setIsVenmoEligible(false);
      } finally {
        setHasCheckedVenmo(true);
      }
    };

    checkVenmoEligibility();
  }, [isResolved, hasCheckedVenmo]);

  // Check Apple Pay eligibility via PayPal SDK. Skipped for decrypted-flag
  // merchants — they run Apple Pay through the VGS path, not PayPal's SDK.
  useEffect(() => {
    if (
      !isResolved ||
      hasCheckedApplePay ||
      !shouldShowApplePayProp ||
      isDecryptedApplePayViaPaypal
    )
      return;

    const checkApplePayEligibility = async () => {
      try {
        const windowObj = window as unknown as Record<string, unknown>;
        const paypal = windowObj.paypal_checkout_global || windowObj.paypal;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (paypal && typeof (paypal as any).Applepay === "function") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const applepay = (paypal as any).Applepay();
          const config = await applepay.config();
          setIsApplePayEligible(!!config?.isEligible);
        } else {
          setIsApplePayEligible(false);
        }
      } catch (e) {
        console.error("Apple Pay eligibility check failed:", e);
        setIsApplePayEligible(false);
      } finally {
        setHasCheckedApplePay(true);
      }
    };

    checkApplePayEligibility();
  }, [
    isResolved,
    hasCheckedApplePay,
    shouldShowApplePayProp,
    isDecryptedApplePayViaPaypal,
  ]);

  const shouldShowVenmo = shouldShowVenmoProp && isVenmoEligible;
  const shouldShowApplePay =
    shouldShowApplePayProp &&
    isApplePayEligible &&
    !isDecryptedApplePayViaPaypal;

  return (
    <PaypalContext.Provider
      value={{
        shouldShowVenmo,
        shouldShowPayLater,
        shouldShowApplePay,
        isProviderReady: true,
        isRejected,
        isDecryptedApplePayViaPaypal,
      }}
    >
      {children}
    </PaypalContext.Provider>
  );
}

export function PaypalProvider({
  cartToken,
  paymentMethodsIncludes,
  children,
}: {
  cartToken: string;
  paymentMethodsIncludes?: string | null;
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();

  const cachedCartData = queryClient.getQueryData<CartResponse>([
    "cart",
    cartToken,
    paymentMethodsIncludes,
  ]);

  const { data: serverCartData, isLoading } = useQuery({
    queryKey: ["cart", cartToken, paymentMethodsIncludes],
    queryFn: () => fetchCart(cartToken, paymentMethodsIncludes ?? undefined),
    enabled: !!cartToken && !cachedCartData,
  });

  const cartData = cachedCartData || serverCartData;

  const paypalMethod = cartData?.cart?.available_payment_methods?.find(
    (method) => method.type === "paypal",
  );
  const clientId = paypalMethod?.client_id;
  const merchantId = paypalMethod?.merchant_id;
  const applePayEnabledInCart = paypalMethod?.apple_pay === true;

  const cartCurrency = cartData?.cart?.currency_code || "USD";

  const hasSubscriptions =
    cartData?.cart?.items?.some((item) => item.subscription) || false;
  const companyId = cartData?.cart?.company?.id;
  const subscriptionAllowed =
    !hasSubscriptions || isApplePaySubscriptionEnabled(companyId);
  const isUSA =
    cartData?.cart?.country?.iso === "US" ||
    cartData?.cart?.country?.iso === "USA";

  const shouldShowVenmo = isUSA && subscriptionAllowed;
  const shouldShowPayLater = subscriptionAllowed;

  // Check if Apple Pay is potentially available (cart config + browser support + merchant ID)
  const shouldShowApplePay = useMemo(() => {
    if (typeof window === "undefined") return false;

    // First check: Cart must have apple_pay enabled on PayPal payment method
    if (!applePayEnabledInCart) return false;

    if (!subscriptionAllowed) return false;
    if (!merchantId) return false;

    // Check if we're in a secure context
    if (!window.isSecureContext) return false;

    // Check for Safari's ApplePaySession OR PaymentRequest API (Chrome/Firefox)
    const hasSafariApplePay = !!(
      window.ApplePaySession && ApplePaySession.canMakePayments()
    );
    const hasPaymentRequest = "PaymentRequest" in window;

    return hasSafariApplePay || hasPaymentRequest;
  }, [applePayEnabledInCart, subscriptionAllowed, merchantId]);

  const initialOptions = useMemo((): ReactPayPalScriptOptions | null => {
    if (!clientId) {
      return null;
    }

    // Always request venmo eligibility from the SDK — PayPal does its own
    // geo/account checks via isFundingEligible(). The UI-level isUSA guard
    // (shouldShowVenmo) controls display. Without this, the one-shot SDK
    // load can permanently miss venmo if the cart country isn't resolved yet.
    const enabledFunding: string[] = ["venmo", "paylater"];

    const options: ReactPayPalScriptOptions = {
      clientId,
      components: "buttons,marks,funding-eligibility,messages,applepay",
      intent: "authorize",
      currency: cartCurrency,
      dataNamespace: "paypal_checkout_global",
      dataPageType: "checkout",
    };

    if (merchantId) {
      options["merchant-id"] = merchantId;
    }

    options["enable-funding"] = enabledFunding.join(",");

    return options;
  }, [clientId, merchantId, cartCurrency]);

  if (isLoading || !initialOptions) {
    return (
      <PaypalContext.Provider
        value={{
          shouldShowVenmo: false,
          shouldShowPayLater: false,
          shouldShowApplePay: false,
          isProviderReady: false,
          isRejected: false,
        }}
      >
        {children}
      </PaypalContext.Provider>
    );
  }

  return (
    <PayPalScriptProvider options={initialOptions}>
      <PaypalProviderInner
        shouldShowVenmo={shouldShowVenmo}
        shouldShowPayLater={shouldShowPayLater}
        shouldShowApplePay={shouldShowApplePay}
        isDecryptedApplePayViaPaypal={
          paypalMethod?.decrypted_apple_pay_via_paypal
        }
      >
        {children}
      </PaypalProviderInner>
    </PayPalScriptProvider>
  );
}
